import React from "react";
import styled from "styled-components";
import { CalendarToday, Group, Launch, GitHub } from "@mui/icons-material";

const Card = styled.div`
  width: 330px;
  height: 480px;
  background: ${({ theme }) => theme.card};
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  border: 1px solid ${({ theme }) => theme.primary + 20};
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.3);
    border-color: ${({ theme }) => theme.primary};
    
    &::after {
      opacity: 1;
    }
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${({ theme }) => theme.primary}, ${({ theme }) => theme.secondary});
    opacity: 0;
    transition: opacity 0.3s ease;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 160px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const CategoryBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: ${({ theme, category }) => 
    category === 'web app' ? theme.primary : 
    category === 'machine learning' ? '#10B981' : 
    theme.secondary};
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 2;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  margin: 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ theme }) => theme.text_secondary + '99'};
  font-size: 12px;
`;

const DateInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Description = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.text_secondary + 'CC'};
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const Tag = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.primary};
  background: ${({ theme }) => theme.primary + '15'};
  padding: 3px 8px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.primary + '30'};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.primary + '25'};
    transform: translateY(-1px);
  }
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: auto;
`;

const TeamSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.primary + '20'};
`;

const TeamInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.text_secondary + '99'};
  font-size: 12px;
`;

const Avatars = styled.div`
  display: flex;
`;

const Avatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid ${({ theme }) => theme.card};
  margin-left: -8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  
  &:first-child {
    margin-left: 0;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const ViewButton = styled.a`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  min-width: 0;
  background: ${({ theme }) => theme.primary};
  color: white;
  border: 1px solid ${({ theme }) => theme.primary};
  
  &:hover {
    background: ${({ theme }) => theme.primary + 'DD'};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${({ theme }) => theme.primary + '40'};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  & svg {
    font-size: 16px;
  }
`;

const CodeButton = styled.a`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  min-width: 0;
  background: ${({ theme }) => theme.bgLight};
  color: ${({ theme }) => theme.text_primary};
  border: 1px solid ${({ theme }) => theme.primary + 30};
  
  &:hover {
    background: ${({ theme }) => theme.primary + '10'};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  ${({ disabled, theme }) => disabled && `
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
    background: ${theme.text_secondary + '10'} !important;
    color: ${theme.text_secondary + '60'} !important;
    border-color: ${theme.text_secondary + '20'} !important;
    
    &:hover {
      background: ${theme.text_secondary + '10'} !important;
      transform: none !important;
      box-shadow: none !important;
    }
  `}
  
  & svg {
    font-size: 16px;
  }
`;

const ProjectCard = ({ project, setOpenModal }) => {
  const handleViewDetails = (e) => {
    e.stopPropagation();
    setOpenModal({ state: true, project: project });
  };

  const handleGitHubClick = (e) => {
    e.stopPropagation();
    if (project.github) {
      window.open(project.github, '_blank');
    }
  };

  return (
    <Card>
      <ImageContainer>
        <Image src={project.image} alt={project.title} />
        <CategoryBadge category={project.category}>
          {project.category === 'web app' ? 'Web' : 
           project.category === 'machine learning' ? 'AI/ML' : 'Project'}
        </CategoryBadge>
      </ImageContainer>
      
      <Content>
        <div>
          <Title>{project.title}</Title>
          <MetaInfo>
            <DateInfo>
              <CalendarToday sx={{ fontSize: 12 }} />
              {project.date}
            </DateInfo>
            {project.member && project.member.length > 0 && (
              <TeamInfo>
                <Group sx={{ fontSize: 12 }} />
                {project.member.length}
              </TeamInfo>
            )}
          </MetaInfo>
        </div>
        
        <Description>{project.description}</Description>
        
        <Tags>
          {project.tags?.slice(0, 4).map((tag, index) => (
            <Tag key={index}>{tag}</Tag>
          ))}
          {project.tags?.length > 4 && (
            <Tag>+{project.tags.length - 4}</Tag>
          )}
        </Tags>
      </Content>
      
      <Footer>
        <TeamSection>
          <Avatars>
            {project.member?.slice(0, 3).map((member, index) => (
              <Avatar 
                key={index} 
                src={member.img} 
                alt={member.name}
                title={member.name}
              />
            ))}
            {project.member?.length > 3 && (
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: '2px solid #fff',
                marginLeft: '-8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '10px',
                fontWeight: 'bold'
              }}>
                +{project.member.length - 3}
              </div>
            )}
          </Avatars>
          
          {project.member?.length > 0 && (
            <TeamInfo>
              <Group sx={{ fontSize: 12 }} />
              {project.member.length} member{project.member.length > 1 ? 's' : ''}
            </TeamInfo>
          )}
        </TeamSection>
        
        <ActionButtons>
          <CodeButton 
            href={project.github || "#"}
            target={project.github ? "_blank" : undefined}
            rel="noopener noreferrer"
            onClick={handleGitHubClick}
            disabled={!project.github}
            title={project.github ? "View source code on GitHub" : "Source code not available"}
          >
            <GitHub />
            Code
          </CodeButton>
          
          <ViewButton 
            href="#"
            onClick={handleViewDetails}
            title="View project details"
          >
            <Launch />
            View
          </ViewButton>
        </ActionButtons>
      </Footer>
    </Card>
  );
};

export default ProjectCard;